import type { OptionsOrGroups } from "react-select";

import type { GroupBase } from "@braze/beacon-lab-select";

import type { NestedGroup } from "../types";

const isNestedGroup = (item: unknown): item is NestedGroup =>
  item != null && typeof item === "object" && "items" in item;

/**
 * Recursively collects [key, option] entries from a list of children,
 * building up the key prefix (e.g. "Group 1 - Group 2 - Option") as it descends.
 */
const collectEntries = <OptionType>(
  children: readonly unknown[],
  formatOptionStringLabel: (option: OptionType) => string,
  prefix: string
): [string, OptionType][] => {
  return children.flatMap((child) => {
    if (child == null) {
      return [];
    }

    if (isNestedGroup(child)) {
      const childLabel = child.name ?? child.label ?? "";
      const nextPrefix = prefix ? `${prefix} - ${childLabel}` : childLabel;
      return collectEntries<OptionType>(child.items ?? [], formatOptionStringLabel, nextPrefix);
    }

    const optionLabel = formatOptionStringLabel(child as OptionType);
    const key = prefix ? `${prefix} - ${optionLabel}` : optionLabel;
    return [[key, child as OptionType]];
  });
};

/**
 * Creates a map of option value strings to option objects.
 * Recursively traverses nested groups (top-level uses `options`, nested use `items`).
 */
export const makeOptionsMap = <OptionType>(
  options: OptionsOrGroups<OptionType, GroupBase<OptionType>>,
  formatOptionStringLabel: (option: OptionType) => string
): Record<string, OptionType> => {
  return Object.fromEntries(
    options.flatMap((optionOrGroup) => {
      // Skip null/undefined values to prevent crashes when formatOptionStringLabel
      // is called on non-existent values
      if (optionOrGroup == null) {
        return [];
      }

      if (typeof optionOrGroup === "object" && "options" in optionOrGroup) {
        const groupLabel = optionOrGroup.label ?? "";
        return collectEntries<OptionType>(optionOrGroup.options, formatOptionStringLabel, groupLabel);
      }

      return [[formatOptionStringLabel(optionOrGroup), optionOrGroup as OptionType]];
    })
  );
};
